import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import clientLogo from "../../../assets/logos/CHULille_Logo.png";
import { signIn } from "../session";
import { normaliseError, errorMessageKey } from "../../../lib/apiError";
import { hardRedirect } from "../../../lib/navigation";
import { PasswordField } from "../../../components/PasswordField";

type FormValues = { email: string; password: string };

type LocationState = { from?: { pathname?: string } };

/**
 * Reasons another page may hand over, as `/signin?notice=…`.
 *
 * A closed set mapped to i18n keys, NOT free text from the query string: a URL
 * anyone can craft must never be able to put a sentence of its own choosing
 * into a green success banner on the login page.
 */
const ARRIVAL_NOTICES = {
  "password-changed": "auth:signIn.noticePasswordChanged",
  "signed-out": "auth:signIn.noticeSignedOut",
} as const;

type NoticeCode = keyof typeof ARRIVAL_NOTICES;

const noticeKey = (value: string | null) =>
  value && value in ARRIVAL_NOTICES
    ? ARRIVAL_NOTICES[value as NoticeCode]
    : null;

export default function SignInPage() {
  const { t } = useTranslation(["auth", "common", "errors"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  // `from` is router state from RequireAuth; the arrival notice is a query
  // parameter because router state does not survive that guard — see the
  // comment in AccountPage.signOutTo.
  const from = (location.state as LocationState | null)?.from?.pathname;

  const [searchParams] = useSearchParams();
  const [notice] = useState(() => noticeKey(searchParams.get("notice")));
  const [noticeOpen, setNoticeOpen] = useState(Boolean(notice));

  useEffect(() => {
    if (!notice) return;
    // Consume it: a reload should not resurrect a confirmation from ten
    // minutes ago. `from` lives in router state and is preserved.
    void navigate(location.pathname, {
      replace: true,
      state: location.state,
    });
    // Once, on arrival — this effect changes the location itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { email: "", password: "" } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values.email.trim(), values.password);
      // A full document navigation, NOT navigate(): it is what makes Safari
      // offer to save the password. See src/lib/navigation.ts.
      hardRedirect(from && from !== "/signin" ? from : "/");
    } catch (error) {
      setFormError(t(errorMessageKey(normaliseError(error))));
    }
  });

  return (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} component="form" onSubmit={onSubmit} noValidate>
          <Box
            component="img"
            src={clientLogo}
            alt=""
            sx={{ height: 44, alignSelf: "flex-start", objectFit: "contain" }}
          />

          <Typography variant="h2" component="h1">
            {t("auth:signIn.title")}
          </Typography>

          {formError ? <Alert severity="error">{formError}</Alert> : null}

          <TextField
            {...register("email", {
              required: t("auth:validation.emailRequired"),
              pattern: {
                value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                message: t("auth:validation.emailInvalid"),
              },
            })}
            label={t("auth:signIn.email")}
            type="email"
            autoComplete="username"
            autoFocus
            fullWidth
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
          />

          <PasswordField
            {...register("password", {
              required: t("auth:validation.passwordRequired"),
            })}
            label={t("auth:signIn.password")}
            autoComplete="current-password"
            fullWidth
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            // The 32px pill from the original design, kept for this CTA only.
            sx={{ borderRadius: 8, alignSelf: "flex-start", px: 5 }}
          >
            {t("auth:signIn.submit")}
          </Button>

          <Stack spacing={1}>
            <MuiLink component={Link} to="/forgot-password" variant="body2">
              {t("auth:signIn.forgot")}
            </MuiLink>
            {/* Stated up front: the lockout is 3 attempts / 3 hours and there
                is no way to self-serve out of it. */}
            <Typography variant="caption" color="text.secondary">
              {t("auth:signIn.lockoutNotice")}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>

      {/* A toast rather than an inline alert: it animates in, so it reads as
          the answer to what just happened instead of as part of the page. */}
      <Snackbar
        open={noticeOpen}
        autoHideDuration={8000}
        onClose={() => setNoticeOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setNoticeOpen(false)}
          sx={{ width: "100%" }}
        >
          {notice ? t(notice) : null}
        </Alert>
      </Snackbar>
    </Card>
  );
}
